import { Box, Flex, Image, Show, useToken } from '@chakra-ui/react';
import { type FC, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { Close } from '@/assets/icons';
import WarningRobo from '@/assets/icons/WarningRobo';
import AppCrashReportDetails from '@/components/Templates/AppCrashReport/AppCrashReportTable/AppCrashReportDetails';
import usePermission from '@/hooks/usePermission';
import type { AppCrashReport } from '@/services/appCrashReport';
import { formatDateTime } from '@/utils';
import { getFriendlyErrorMessage } from '@/utils/friendlyErrorMessages';
import packageJson from '../../../package.json';
import FaceliftButton from '../Atoms/FaceliftButton';
import FaceliftDialog from '../Atoms/FaceliftDialog';
import FaceliftText from '../Atoms/FaceliftText';
import { colors } from './theme';

type IAppCrashProviderProps = {
	userId: string | null;
};
const baseURL = `${import.meta.env.VITE_CRASH_URL}`;

const AppCrashProvider: FC<IAppCrashProviderProps> = ({ userId }) => {
	const [brand600] = useToken('colors', ['brand.600']);
	const { hasPermission } = usePermission();
	const nodeRef = useRef(null);
	const [position, setPosition] = useState({ x: 0, y: 20 });
	const [startPosition, setStartPosition] = useState({ x: 0, y: 20 });
	const [isDragging, setIsDragging] = useState(false);


	//States for feature logic
	const [appCrashReport, setAppCrashReport] = useState<AppCrashReport | null>(
		null,
	);
	const [open, setOpen] = useState(false);
	// Modal state for details view
	const [modalInformation, setModalInformation] = useState<{
		mode: 'details' | null;
		data: string | null;
	}>({
		mode: null,
		data: null,
	});

	const handleCloseModal = () => {
		setModalInformation({ mode: null, data: null });
	};

	const hasAPPCrashLogsPermission = hasPermission(['APPCrashLogs:Visualize']);
	const hasTechnicalAlertsPermission = hasPermission(['TechnicalAlerts:Visualize']);
	useEffect(() => {
		if (!userId || !hasAPPCrashLogsPermission) return;
		// if (env === 'develop') return;
		const eventSource = new EventSource(
			`${baseURL}/app-crash-log/stream/${userId}`,
			{
				withCredentials: false,
			},
		);

		eventSource.onmessage = (event) => {
			const data = JSON.parse(event.data);
			if (data?._id) {
				setAppCrashReport(data);
			}
		};

		eventSource.onerror = (err) => {
			console.error('SSE Error:', err);
			eventSource.close();
		};

		return () => eventSource.close();
	}, [userId, packageJson.version, hasAPPCrashLogsPermission]);

	//Reset the position on screen size change
	useEffect(() => {
		const setInitialPosition = () => {
			const initialX = window.innerWidth - 130; // 80 is button width
			const initialY = 140; // 20px down from top bar
			setPosition({ x: initialX, y: initialY });
			setStartPosition({ x: initialX, y: initialY });
		};

		// Set initial position on mount
		setInitialPosition();

		// Reset position on window resize
		window.addEventListener('resize', setInitialPosition);
		return () => window.removeEventListener('resize', setInitialPosition);
	}, []);

	// Close dialog and modal when user logs out
	useEffect(() => {
		if (!userId) {
			setOpen(false);
			handleCloseModal();
			setAppCrashReport(null);
		}
	}, [userId]);

	const hiddenRoutes = ['/login', '/choose-platform'];
	const isVisible = window?.location
		? Boolean(userId) &&
			!hiddenRoutes.includes(window.location.pathname) &&
			hasAPPCrashLogsPermission
		: Boolean(userId) && hasAPPCrashLogsPermission;

	const friendlyError = appCrashReport?.errorMessage && !hasTechnicalAlertsPermission
		? getFriendlyErrorMessage(appCrashReport.errorMessage)
		: null;

	return (
		<Box position={'relative'}>
			<Show when={isVisible && appCrashReport?._id}>
				<Draggable
					nodeRef={nodeRef}
					axis="both"
					bounds={{
						left: 0,
						top: 10,
						right: window.innerWidth - 130,
						bottom: window.innerHeight - 100,
					}}
					position={position}
					onStart={(_e, data) => {
						setStartPosition({ x: data.x, y: data.y });
						setIsDragging(false);
					}}
					onDrag={() => setIsDragging(true)}
					onStop={(_e, data) => {
						const withinYBounds =
							data.y > 2 && data.y < window.innerHeight - 120;
						if (withinYBounds) {
							if (data.x > window.innerWidth / 2) {
								setPosition({ x: window.innerWidth - 130, y: data.y });
							} else {
								setPosition({ x: 0, y: data.y });
							}
						} else {
							setPosition(startPosition);
						}
					}}
				>
					<Box
						ref={nodeRef}
						position="absolute"
						zIndex={9}
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
						gap={2}
						cursor="grab"
						onClick={() => {
							if (!isDragging) {
								setOpen(true);
							}
						}}
					>
						<Box
							fontSize={12}
							bg="red"
							px={2}
							py={1}
							borderRadius="full"
							color="white"
						>
							Something Crashed
						</Box>
						<Box
							bg="white"
							borderRadius="full"
							p={3}
							boxShadow={`0 0 20px 0px ${colors.errors}`}
							position="relative"
						>
							<Box
								position="absolute"
								top="0px"
								right="0px"
								onClick={(e) => {
									e.stopPropagation();
									setAppCrashReport(null);
								}}
								cursor="pointer"
								p={1.5}
								borderRadius="full"
								bg="white"
								_hover={{
									bg: { base: 'gray.200', _dark: 'gray.300' },
								}}
								boxShadow={`0 0 20px 0px ${colors.errors}`}
								zIndex={1}
							>
								<Close height={8} width={8} />
							</Box>
							<Image
								src={'/gifs/Warning.gif'}
								width={12}
								height={12}
								borderRadius="50%"
								draggable={false}
							/>
						</Box>
					</Box>
				</Draggable>
			</Show>

			<FaceliftDialog
				open={open}
				onOpenChange={(open) => {
					if (!open) {
						setOpen(false);
					}
				}}
				size="xl"
				sx={{
					width: 800,
					maxWidth: '90vw',
					maxHeight: '90vh',
					boxShadow: `0 0 20px 0px ${colors.errors}`,
					borderRadius: '12px',
					backdropFilter: 'blur(10px)',
					transition: 'top 0.2s ease, left 0.2s ease',
				}}
			>
				<Box pt={5}>
					<Flex
						direction={{ base: 'column', md: 'column', lg: 'row' }}
						gap={{ base: 8, md: 4 }}
						align={{ base: 'center', md: 'center', lg: 'flex-start' }}
					>
						<Flex
							flex={{ base: 'none', md: '0 0 28.57%', lg: '0 0 28.57%' }}
							justifyContent={{ base: 'center', md: 'center' }}
							alignItems={{ base: 'center', md: 'center', lg: 'flex-start' }}
							w={{ base: '50%', md: '50%', lg: 'auto' }}
						>
							<Box position="relative">
								<Box>
									<WarningRobo />
								</Box>
								<Box position="absolute" bottom="50px" right="50px">
									<Image src={'/gifs/Warning.gif'} width={24} height={24} />
								</Box>
							</Box>
						</Flex>
						<Flex
							flex={{ base: 'none', md: '1 1 71.43%', lg: '1 1 71.43%' }}
							direction="column"
							gap={2}
							w={{ base: '100%', md: 'auto' }}
							maxW="100%"
						>
							<FaceliftText
								fontSize={'2xl'}
								fontWeight={'bold'}
								wordBreak="break-word"
							>
								{hasTechnicalAlertsPermission
									? 'We Hit a Crash'
									: friendlyError?.title || 'We Hit a Crash'}
							</FaceliftText>
							<FaceliftText wordBreak="break-word">
								{hasTechnicalAlertsPermission
									? `An unexpected error occurred while loading this section. The issue has been logged automatically so our team can investigate it.`
									: friendlyError?.message ||
										`An unexpected error occurred while loading this section. The issue has been logged automatically so our team can investigate it.`}
							</FaceliftText>

							<Box
								border={'1px solid'}
								borderColor={'gray.300'}
								borderRadius={8}
								bg={{ base: '#F3FFFD', _dark: '#21233c' }}
								p={4}
								maxW="100%"
								overflow="hidden"
							>
								{appCrashReport?.errorMessage && (
									<FaceliftText
										fontStyle="italic"
										color={{ base: 'gray.900', _dark: 'gray.300' }}
										wordBreak="break-word"
									>
										<FaceliftText as="span" fontWeight={'bold'}>
											Error Summary:{' '}
										</FaceliftText>{' '}
										{hasTechnicalAlertsPermission
											? appCrashReport.errorMessage
											: friendlyError?.message || appCrashReport.errorMessage}
									</FaceliftText>
								)}
								<Box
									display="flex"
									gap={2}
									fontStyle="italic"
									color={{ base: 'gray.900', _dark: 'gray.300' }}
								>
									<FaceliftText as="span" fontWeight={'bold'}>
										Module Affected:
									</FaceliftText>
									<FaceliftText>{appCrashReport?.moduleName}</FaceliftText>
								</Box>
								<Box
									display="flex"
									gap={2}
									fontStyle="italic"
									color={{ base: 'gray.900', _dark: 'gray.300' }}
								>
									<FaceliftText as={'span'} fontWeight={'bold'}>
										Timestamp:
									</FaceliftText>
									<FaceliftText>
										{formatDateTime(appCrashReport?.createdAt)}
									</FaceliftText>
								</Box>
							</Box>

							<Box
								direction={{ base: 'column', md: 'row', lg: 'row' }}
								gap={{ base: 2, md: 2 }}
								display="flex"
								flexWrap="wrap"
							>
								{friendlyError?.action && (
									<FaceliftButton
										bg={'transparent'}
										width={{ base: '100%', md: 'auto' }}
										size={'sm'}
										color={'inherit'}
										border={'1px solid'}
										onClick={() => {
											if (friendlyError.actionRoute) {
												window.location.href = friendlyError.actionRoute;
											} else if (friendlyError.action === 'Refresh Page') {
												window.location.reload();
											} else {
												setOpen(false);
											}
										}}
									>
										{friendlyError.action}
									</FaceliftButton>
								)}
								<FaceliftButton
									bg={'transparent'}
									width={{ base: '100%', md: 'auto' }}
									size={'sm'}
									color={'inherit'}
									border={'1px solid'}
									onClick={() =>
										setModalInformation({
											mode: 'details',
											data: appCrashReport?._id,
										})
									}
								>
									Details
								</FaceliftButton>
							</Box>
						</Flex>
					</Flex>
				</Box>
			</FaceliftDialog>

			<FaceliftDialog
				showBackBtn={true}
				title="Details"
				open={modalInformation.mode === 'details'}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				sx={{
					width: '70vw',
					maxWidth: '70vw',
					maxHeight: '90vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowY: 'auto',
				}}
			>
				<AppCrashReportDetails
					key={modalInformation.data}
					id={modalInformation.data}
				/>
			</FaceliftDialog>
		</Box>
	);
};

export default AppCrashProvider;
